/**
 * M16 — welcome back (S4, one open). A recap of what happened since
 * `lastAppOpen`, one line, expandable in place into the modules it
 * summarises (`expandsInto`, rendered through the registry so they are the
 * same components they would be on the desk). Never dismissable.
 *
 * The line is assembled from counts the payload carries — nothing is
 * asserted that did not happen. `datesPassed[].kind` decides "birthdays"
 * vs the neutral "dates".
 */

import { useState } from 'react';
import { HomeCard, HomeChevron } from '../HomeKit';
import { HomeModuleView } from '../moduleRegistry';
import { plural, sentence, sinceLabel, smallNumber } from '../format';
import type { ModuleProps } from '../moduleTypes';

function countWord(n: number, one: string, many?: string): string {
  return `${smallNumber(n)} ${n === 1 ? one : many ?? `${one}s`}`;
}

export function WelcomeBackModule({ module, onAction }: ModuleProps) {
  const [expanded, setExpanded] = useState(false);
  if (module.type !== 'welcome_back') return null;
  const { payload } = module;

  const parts: string[] = [];
  if (payload.datesPassed.length) {
    const allBirthdays = payload.datesPassed.every((d) => d.kind === 'birthday');
    parts.push(`${countWord(payload.datesPassed.length, allBirthdays ? 'birthday' : 'date')} passed`);
  }
  if (payload.reviewCount > 0) parts.push(`${countWord(payload.reviewCount, 'thing')} to review`);
  if (payload.roomsWaiting.length === 1) parts.push(`${payload.roomsWaiting[0].personName} is waiting`);
  else if (payload.roomsWaiting.length > 1) parts.push(`${plural(payload.roomsWaiting.length, 'room')} waiting`);
  if (payload.trialChange === 'ended') parts.push('your trial ended');

  const body = parts.length
    ? sentence(parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} — and ${parts[parts.length - 1]}`)
    : 'Welcome back';
  const sub = expanded ? undefined : parts.length ? 'tap anything below, or just say “catch me up”' : `it’s been ${sinceLabel(payload.sinceDays)}`;

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    onAction?.({ kind: 'expand', dismissKey: module.dismissKey, expanded: next }, module);
  };

  return (
    <>
      <HomeCard
        hi
        reason={module.reason}
        body={body}
        sub={sub}
        right={payload.expandsInto.length ? <HomeChevron dir={expanded ? 'up' : 'down'} /> : undefined}
        dismissable={false}
        onOpen={payload.expandsInto.length ? toggle : undefined}
      />
      {expanded && payload.expandsInto.length ? (
        <div className="home-expanded">
          {payload.expandsInto.map((m) => (
            <HomeModuleView key={m.dismissKey} module={m} onAction={onAction} />
          ))}
        </div>
      ) : null}
    </>
  );
}
